import { type SyntheticEvent, useCallback, useState } from 'react';

import { useNormalizedSvgPreview } from '~/hooks/queries/useNormalizedSvgPreview';
import { isSvgExtension } from '~/utils/getTransparentPreviewSource';

export type UseInlineSvgPreviewParams = {
  src: string | undefined;
  ext: string | undefined;
  /** Surface-specific conditions; the SVG extension check is applied here. */
  enabled: boolean;
};

export type UseInlineSvgPreviewResult = {
  inlineSvgPreviewUrl: string | undefined;
  /** With an event, the failure only counts when the failing element was showing the data URL. */
  markInlineSvgPreviewFailed: (event?: SyntheticEvent<HTMLImageElement>) => void;
  /** True while the normalized preview is being fetched; false once it settles (with or without a preview). */
  isInlineSvgPreviewPending: boolean;
};

/**
 * Imgix returns SVG originals raw (no fonts, no intrinsic size), so SVG previews display a normalized data URL
 * instead. A data URL that fails to load is dropped so the caller falls back to its Imgix image.
 */
export const useInlineSvgPreview = ({ src, ext, enabled }: UseInlineSvgPreviewParams): UseInlineSvgPreviewResult => {
  const [failedSvgPreviewUrl, setFailedSvgPreviewUrl] = useState<string | undefined>();
  const isEnabled = isSvgExtension(ext) && enabled;
  // Disabled callers share a single skipToken entry instead of one per URL.
  const { data, isLoading } = useNormalizedSvgPreview({ src: isEnabled ? src : undefined }, { enabled: isEnabled });
  const svgPreviewUrl = data ?? undefined;
  const inlineSvgPreviewUrl = svgPreviewUrl !== failedSvgPreviewUrl ? svgPreviewUrl : undefined;

  const markInlineSvgPreviewFailed = useCallback(
    (event?: SyntheticEvent<HTMLImageElement>) => {
      // A late error from the Imgix image this data URL replaced must not discard a working preview.
      if (event && (event.currentTarget.currentSrc || event.currentTarget.src) !== inlineSvgPreviewUrl) {
        return;
      }

      setFailedSvgPreviewUrl(svgPreviewUrl);
    },
    [inlineSvgPreviewUrl, svgPreviewUrl],
  );

  // `isLoading` rather than `isPending`: a skipToken query stays `isPending` forever.
  return { inlineSvgPreviewUrl, markInlineSvgPreviewFailed, isInlineSvgPreviewPending: isLoading };
};
